import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowPathIcon,
  XMarkIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowsPointingInIcon,
  ArrowRightIcon,
  CheckIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import {
  listSubcategories,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory,
  mergeSubcategory,
} from "../../api/categoriesAdmin";

const fmt = (n) => Number(n || 0).toLocaleString("ru-RU");
const inputCls =
  "w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary " +
  "placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30";
const iconBtn = "rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary disabled:opacity-30";
const NAME_FIELDS = [
  ["name", "Название (RU) *"],
  ["kz_name", "Атауы (KZ)"],
  ["eng_name", "Name (EN)"],
  ["ch_name", "名称 (CN)"],
];
const emptyForm = { name: "", kz_name: "", eng_name: "", ch_name: "" };
const toBody = (form) => Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim() || (k === "name" ? v : null)]));

/** Подкатегория формасы: атаулар (+ басқа категорияға көшіру — тек өңдеуде). */
const SubForm = ({ initial, categories, currentCategoryId, onCancel, onSubmit, submitLabel }) => {
  const [form, setForm] = useState({ ...emptyForm, ...Object.fromEntries(Object.keys(emptyForm).map((k) => [k, initial?.[k] || ""])) });
  const [categoryId, setCategoryId] = useState(currentCategoryId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("Название (RU) обязательно.");
    setBusy(true);
    setError(null);
    try {
      const body = toBody(form);
      if (initial && Number(categoryId) !== currentCategoryId) body.category_id = Number(categoryId);
      await onSubmit(body);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-[#4F45E5]/40 bg-bg-primary p-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {NAME_FIELDS.map(([k, l], i) => (
          <label key={k} className="block">
            <span className="mb-1 block text-[11px] font-medium text-text-secondary">{l}</span>
            <input autoFocus={i === 0} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className={inputCls} />
          </label>
        ))}
      </div>
      {initial && (
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-text-secondary">Категория</span>
          <select value={categoryId} onChange={(e) => setCategoryId(Number(e.target.value))} className={inputCls}>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} #{c.id}
                {c.is_hidden ? " (скрыта)" : ""}
              </option>
            ))}
          </select>
          {Number(categoryId) !== currentCategoryId && (
            <span className="mt-1 block text-[11px] text-amber-500">
              Объявления и заявки этой подкатегории ({fmt(initial.announcements)} / {fmt(initial.demands)}) перейдут в новую категорию.
            </span>
          )}
        </label>
      )}
      {error && <p className="text-sm text-rose-500">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="rounded-lg border border-border-primary px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary">
          Отмена
        </button>
        <button type="submit" disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-accent-grad px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
          {busy ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
};

/** Біріктіру: бір категория ішінде (әдепкі) немесе кез келген категорияның подкатегориясына. */
const MergePanel = ({ source, items, onCancel, onDone }) => {
  const candidates = items.filter((s) => s.id !== source.id);
  const preferred = source.duplicates.find((d) => candidates.some((c) => c.id === d));
  const [targetId, setTargetId] = useState(preferred || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const target = candidates.find((s) => s.id === Number(targetId));
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await mergeSubcategory(source.id, target.id);
      onDone(`«${source.name}» → «${target.name}»: объявлений ${fmt(r.announcements_moved)}, заявок ${fmt(r.demands_moved)}`);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-lg border border-amber-500/40 bg-amber-500/[0.06] p-3">
      <p className="text-sm text-text-primary">
        Объединить «{source.name}» <span className="font-mono text-text-muted">#{source.id}</span> с:
      </p>
      <select value={targetId} onChange={(e) => setTargetId(e.target.value)} className={inputCls}>
        <option value="">— Выберите подкатегорию —</option>
        {candidates.map((s) => (
          <option key={s.id} value={s.id}>
            {source.duplicates.includes(s.id) ? "★ " : ""}
            {s.name} #{s.id} · {s.announcements} объявл.
          </option>
        ))}
      </select>
      {target && (
        <p className="text-xs text-text-secondary">
          {fmt(source.announcements)} объявлений и {fmt(source.demands)} заявок перейдут в «{target.name}», а «{source.name}» будет удалена. Отменить нельзя.
        </p>
      )}
      {error && <p className="text-sm text-rose-500">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="rounded-lg border border-border-primary px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary">
          Отмена
        </button>
        <button type="button" onClick={run} disabled={busy || !target} className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-40">
          {busy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}Объединить
        </button>
      </div>
    </div>
  );
};

const SubcategoriesDialog = ({ category, categories, onClose, onChanged }) => {
  const [items, setItems] = useState([]);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null); // { type: "add" } | { type: "edit"|"merge"|"delete", sub }
  const [toast, setToast] = useState(null);
  const [query, setQuery] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      setItems(await listSubcategories(category.id));
      setState("ready");
    } catch (err) {
      setError(err.message);
      setState("error");
    }
  }, [category.id]);

  useEffect(() => {
    load();
  }, [load]);

  const close = () => onClose(dirty);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !mode && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const done = async (text) => {
    setMode(null);
    setToast({ type: "success", text });
    setDirty(true);
    onChanged?.();
    await load();
  };

  const remove = async (sub) => {
    setDeleting(true);
    try {
      await deleteSubcategory(sub.id);
      await done(`«${sub.name}» удалена`);
    } catch (err) {
      setToast({ type: "error", text: err.message });
      setMode(null);
    } finally {
      setDeleting(false);
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = items.filter((s) => !q || `${s.name} ${s.kz_name || ""} #${s.id}`.toLowerCase().includes(q));
  const dupCount = items.filter((s) => s.duplicates.length).length;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && !mode && close()}>
      <aside role="dialog" aria-modal="true" aria-label={`Подкатегории «${category.name}»`} className="flex h-full w-full max-w-2xl flex-col border-l border-border-primary bg-bg-card text-left shadow-xl">
        <header className="flex items-start justify-between gap-3 border-b border-border-primary px-5 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-text-primary">
              Подкатегории · {category.name} <span className="font-mono text-text-muted">#{category.id}</span>
            </h3>
            <p className="text-xs text-text-secondary">
              {items.length} подкатегорий{dupCount ? ` · дублей по названию: ${dupCount}` : ""}
            </p>
          </div>
          <button type="button" onClick={close} aria-label="Закрыть" className="rounded-lg p-1 text-text-muted hover:bg-bg-tertiary">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        <div className="flex flex-wrap items-center gap-2 border-b border-border-primary px-5 py-3">
          <div className="relative min-w-0 flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Название или #ID" aria-label="Поиск подкатегории" className={`${inputCls} pl-9`} />
          </div>
          <button type="button" onClick={() => setMode({ type: "add" })} disabled={mode?.type === "add"} className="inline-flex items-center gap-1.5 rounded-lg bg-accent-grad px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
            <PlusIcon className="h-4 w-4" /> Добавить
          </button>
        </div>

        {toast && (
          <div role={toast.type === "error" ? "alert" : "status"} className={`mx-5 mt-3 flex items-start justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${toast.type === "error" ? "border-rose-500/30 bg-rose-500/10 text-rose-500" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"}`}>
            <span>{toast.text}</span>
            <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {mode?.type === "add" && (
            <div className="mb-3">
              <SubForm
                categories={categories}
                currentCategoryId={category.id}
                submitLabel="Создать"
                onCancel={() => setMode(null)}
                onSubmit={async (body) => {
                  const r = await createSubcategory(category.id, body);
                  await done(`Подкатегория «${r.name}» #${r.id} создана`);
                }}
              />
            </div>
          )}

          {state === "loading" && (
            <ul className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <li key={i} className="h-11 animate-pulse rounded-lg bg-bg-tertiary" />
              ))}
            </ul>
          )}
          {state === "error" && (
            <div className="py-10 text-center text-sm text-text-secondary">
              Не удалось загрузить: {error}
              <button type="button" onClick={load} className="ml-2 underline">
                Повторить
              </button>
            </div>
          )}
          {state === "ready" && (
            <ul className="divide-y divide-border-primary">
              {filtered.map((s) => (
                <li key={s.id} className="py-2">
                  {mode?.sub?.id === s.id && mode.type === "edit" ? (
                    <SubForm
                      initial={s}
                      categories={categories}
                      currentCategoryId={category.id}
                      submitLabel="Сохранить"
                      onCancel={() => setMode(null)}
                      onSubmit={async (body) => {
                        const r = await updateSubcategory(s.id, body);
                        await done(r.category_id !== category.id ? `«${r.name}» перенесена в другую категорию` : `«${r.name}» сохранена`);
                      }}
                    />
                  ) : mode?.sub?.id === s.id && mode.type === "merge" ? (
                    <MergePanel source={s} items={items} onCancel={() => setMode(null)} onDone={done} />
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="truncate text-sm font-medium text-text-primary">{s.name}</span>
                          <span className="font-mono text-[11px] text-text-muted">#{s.id}</span>
                          {s.duplicates.length > 0 && (
                            <span className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 text-[10px] font-semibold text-amber-500">
                              дубль {s.duplicates.map((d) => `#${d}`).join(", ")}
                            </span>
                          )}
                        </div>
                        <div className="truncate text-[11px] text-text-muted">
                          {s.kz_name || "— KZ"} · {s.eng_name || "— EN"}
                        </div>
                      </div>
                      <div className="w-20 text-right">
                        <div className="text-sm tabular-nums text-text-primary">{fmt(s.active_announcements)}</div>
                        <div className="text-[10px] text-text-muted">из {fmt(s.announcements)} объявл.</div>
                      </div>
                      {mode?.sub?.id === s.id && mode.type === "delete" ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-text-secondary">Удалить?</span>
                          <button type="button" disabled={deleting} onClick={() => remove(s)} className="rounded-lg bg-rose-500 px-2.5 py-1 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-50">
                            Да
                          </button>
                          <button type="button" disabled={deleting} onClick={() => setMode(null)} className="rounded-lg border border-border-primary px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-tertiary">
                            Нет
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-0.5">
                          <button type="button" onClick={() => setMode({ type: "edit", sub: s })} title="Редактировать / перенести" aria-label="Редактировать" className={iconBtn}>
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => setMode({ type: "merge", sub: s })} disabled={items.length < 2} title="Объединить с другой подкатегорией" aria-label="Объединить" className={iconBtn}>
                            <ArrowsPointingInIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setMode({ type: "delete", sub: s })}
                            disabled={s.announcements > 0 || s.demands > 0}
                            title={s.announcements > 0 || s.demands > 0 ? "Есть объявления или заявки — сначала объедините" : "Удалить"}
                            aria-label="Удалить"
                            className={`${iconBtn} hover:text-rose-500`}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              ))}
              {filtered.length === 0 && <li className="py-10 text-center text-sm text-text-muted">{items.length ? "Ничего не найдено" : "Подкатегорий пока нет"}</li>}
            </ul>
          )}
        </div>

        <footer className="flex items-center gap-2 border-t border-border-primary px-5 py-3 text-[11px] text-text-muted">
          <ArrowRightIcon className="h-3.5 w-3.5" />
          Удалить можно только пустую подкатегорию. С объявлениями — объедините её с другой.
        </footer>
      </aside>
    </div>
  );
};

export default SubcategoriesDialog;
